import { Button, Checkbox, Col, Divider, Form, Input, Row } from 'antd';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import ReCAPTCHA from 'react-google-recaptcha';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { trackPromise } from 'react-promise-tracker';
import { App } from '@capacitor/app';

import { loginValidation } from '../../validation/login.validation';
import userAuthStore from 'common/stores/user-auth/user-auth.store';
import { IS_BIOMETRIC_AUTH_ENABLED } from 'common/stores/user-auth/user-auth.constants';
import { FormWrapper } from 'common/components/form-wrapper/form-wrapper';
import Spinner from '../../../../common/components/base/spinner/spinner';
import { notification } from '../../../../common/utils/notification';
import {
  loginErrorMessage,
  residentDeletionRequestedErrorMessage,
  RegisteredInDifferentCommunitiesErrorMessage,
  ResidentRentalDateIsNotValidErrorMessage,
} from 'common/constans/alert-messages.constants';
import { ResidentDeletionRequested, ResidentRentalDateIsNotValid } from 'common/constans/errorCodes.constants';
import { getLogin } from 'common/helpers/login.helper';
import healthCheckService from 'common/services/helthcheck-service/helscheck-service';
import featureService from 'common/services/feature-service/feature.service';
import communityResolverService from 'common/services/community-resolver-service/community-resolver.service';
import secureService from 'features/eiq-connect/service/security-service/security.service';
import { ReactComponent as LoginIcon } from 'assets/icons/eiq-connect/login.svg';
import { ReactComponent as EyeIcon } from 'assets/icons/eiq-connect/eye.svg';
import { ReactComponent as EyeCrossIcon } from 'assets/icons/eiq-connect/eye-cross.svg';
import { ReactComponent as FaceId } from 'assets/icons/eiq-connect/faceid.svg';

import './login.scss';

const INVALID_CAPTCHA_MESSAGE = 'Invalid CAPTCHA token. Please complete the CAPTCHA and try again.';

interface IBiometricAuthModel {
  isEnabled: boolean;
  login: string;
}

export const WGLoginForm = observer(() => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const recaptchaRef = useRef<ReCAPTCHA | null>(null);
  const showBiometricRef = useRef(false);

  const [biometricAuth, setBiometricAuth] = useState<IBiometricAuthModel | null>(null);
  const [isBiometricAuthAvailable, setBiometricAuthAvailable] = useState(false);
  const [isAppActive, setAppActive] = useState(false);
  const [useRecaptchaV2, setRecaptchaV2] = useState(false);

  const onBiometricAuthClick = useCallback(async () => {
    if (!biometricAuth?.isEnabled || !biometricAuth.login) {
      return;
    }

    await trackPromise(userAuthStore.passwordlessRequestTokenForResident(biometricAuth.login, () => {}));
  }, [biometricAuth]);

  useEffect(() => {
    if (isAppActive && biometricAuth && isBiometricAuthAvailable && !showBiometricRef.current) {
      showBiometricRef.current = true;
      onBiometricAuthClick();
    }
  }, [isAppActive, biometricAuth, isBiometricAuthAvailable, onBiometricAuthClick]);

  useEffect(() => {
    sessionStorage.removeItem('isSuccessfullySaved');
    notification.destroy();

    const initializeBiometricAuth = async () => {
      const storedBiometricAuth = localStorage.getItem(IS_BIOMETRIC_AUTH_ENABLED);
      const biometricAuthModel = storedBiometricAuth ? (JSON.parse(storedBiometricAuth) as IBiometricAuthModel) : null;

      setBiometricAuth(biometricAuthModel);

      const isAvailable = await secureService.isBiometricAuthAvailable();

      if (!biometricAuthModel?.isEnabled || !biometricAuthModel.login) {
        setBiometricAuthAvailable(false);
        return;
      }

      await communityResolverService.resolve(biometricAuthModel.login);
      const { isEnabled } = await featureService.isConnectFIDEnabled();
      setBiometricAuthAvailable(isAvailable && isEnabled);
    };

    initializeBiometricAuth();

    if (!userAuthStore.manualLogout) {
      setAppActive(true);
    }

    const appStateListener = App.addListener('appStateChange', ({ isActive }) => setAppActive(isActive));

    return () => {
      appStateListener.then(({ remove }) => remove());
    };
  }, []);

  const onLoginFailure = async (error: any) => {
    // TODO: Add a server availability check with different error, after EIQ-1080
    if (!(await healthCheckService.isNetworkConnected())) {
      notification.error({
        message: 'No internet connection. Please check your network and try again.',
      });
      return;
    }

    recaptchaRef.current?.reset();
    notification.destroy();

    const captchaErrorCode = error?.response?.data?.errorCode;

    if (captchaErrorCode === 'InvalidCaptchaV3Token' || captchaErrorCode === 'InvalidCaptchaV2Token') {
      if (captchaErrorCode === 'InvalidCaptchaV3Token') {
        setRecaptchaV2(true);
      }

      notification.error({ message: INVALID_CAPTCHA_MESSAGE, duration: 15 });
      return;
    }

    switch (error?.errorCode) {
      case ResidentDeletionRequested:
        notification.error({ message: residentDeletionRequestedErrorMessage });
        break;
      case ResidentRentalDateIsNotValid:
        notification.error({
          message: ResidentRentalDateIsNotValidErrorMessage,
        });
        break;
      case 'RegisteredInDifferentCommunities':
        notification.error({
          message: RegisteredInDifferentCommunitiesErrorMessage,
        });
        break;
      default:
        notification.error({ message: loginErrorMessage, duration: null });
    }
  };

  const onFinish = async (values: any) => {
    const tokenV3 = useRecaptchaV2 ? null : await executeRecaptcha!('contact_form');

    trackPromise(userAuthStore.requestTokenForResident(getLogin(values.login), values.password, values.recaptcha, tokenV3, onLoginFailure));
  };

  if (userAuthStore.isLogin) {
    return <Navigate to="/eiq-connect" />;
  }

  return (
    <>
      <FormWrapper
        className="eiq-connect-auth-form-wrapper"
        form={form}
        onFinish={onFinish}
        name="validateOnly"
        layout="vertical"
        autoComplete="on"
        initialValues={{ remember: true }}>
        <Row>
          <Col>
            <div className="title-wrapper">
              <Button.Group className="full-width" style={{ display: 'flex' }} size="large">
                <Button style={{ flex: 1, fontWeight: 'bold' }} type="primary">
                  Log in
                </Button>
                <Button style={{ flex: 1, fontWeight: 'bold' }} onClick={() => navigate('/sign-up')}>
                  Account activation
                </Button>
              </Button.Group>
            </div>

            {isBiometricAuthAvailable && (
              <div className="text text-center biometric">
                <FaceId width={48} onClick={onBiometricAuthClick} style={{ cursor: 'pointer' }} />
                <span>Tap here to login using FaceID</span>
              </div>
            )}

            <div className="text">Please use the email you have registered with your association to log in.</div>
          </Col>
        </Row>

        <div>
          <Form.Item name="login" label="Email" rules={loginValidation.login}>
            <Input name="login" placeholder="Enter email" autoCapitalize="none" autoCorrect="off" />
          </Form.Item>

          <Divider className="auth-diver" />

          <Form.Item name="password" label="Password (Min 8 characters)" rules={loginValidation.password}>
            <Input.Password
              size="large"
              name="password"
              placeholder="Enter password"
              iconRender={(visible) => (visible ? <EyeCrossIcon /> : <EyeIcon />)}
            />
          </Form.Item>

          <div className="keep-me-logged-in">
            <span>Keep me logged in</span>
            <Checkbox checked={userAuthStore.keepMeLoggedIn} onChange={({ target }) => userAuthStore.setKeepMeLoggedIn(target.checked)} />
          </div>

          {useRecaptchaV2 && (
            <Form.Item name="recaptcha" rules={[{ required: true, message: INVALID_CAPTCHA_MESSAGE }]}>
              <ReCAPTCHA ref={recaptchaRef} className="recaptcha" sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY ?? ''} />
            </Form.Item>
          )}
        </div>

        <div className="action-line">
          <Button htmlType="submit" type="primary" icon={<LoginIcon />} style={{ width: '100%' }}>
            Log In
          </Button>
        </div>

        <div className="text text-center">
          <Link to="/forgot-password">Forgot the password?</Link>
        </div>
        <div className="text text-center" style={{ marginTop: '-20px' }}>
          {process.env.REACT_APP_RELEASE_NAME || ''}
        </div>
      </FormWrapper>
      <Spinner />
    </>
  );
});
